import React from "react";
import { useNavigate } from "react-router-dom";
import useMaturityData from "../hooks/useMaturityData";
import { LEVEL_COLORS } from "../utils/levelColors";
import "./dashboard.css";

const NextSteps = () => {
  const { levelProgress, loading, error } = useMaturityData();
  const navigate = useNavigate();

  if (loading) return <div className="dashboard-loading">Loading next steps…</div>;
  if (error) return <div className="dashboard-loading">{error}</div>;

  if (!levelProgress) {
    return (
      <>
        <div className="dashboard-header">
          <h1 className="dashboard-title">Next Steps</h1>
        </div>
        <div className="dashboard-next-steps">
          <p className="dashboard-next-steps-text">
            Complete the questionnaire to see how you can improve your maturity level.
          </p>
        </div>
      </>
    );
  }

  const improvableDimensions = Object.entries(levelProgress).filter(
    ([, data]) => data.nextLevel != null
  );

  return (
    <>
      <div className="dashboard-header">
        <h1 className="dashboard-title">Next Steps</h1>
        <p className="dashboard-subtitle">
          Concrete steps to reach the next maturity level in each dimension
        </p>
      </div>

      <div className="dashboard-table-section">
        <h2 className="dashboard-section-title">How to Reach the Next Level</h2>
        <p className="dashboard-table-note">
          For each dimension, expand the panel to see which questionnaire answers
          can be improved and what the next better answer looks like.
        </p>

        {improvableDimensions.length === 0 ? (
          <p className="dashboard-next-steps-text">
            Congratulations — you have reached the highest maturity level in
            every dimension. There is nothing left to improve.
          </p>
        ) : (
          <div className="radar-hints-list">
            {improvableDimensions.map(([dim, data]) => (
              <details key={dim} className="radar-hint-panel">
                <summary className="radar-hint-summary">
                  <span className="radar-hint-dim">{dim}</span>
                  <span className="radar-hint-meta">
                    <span
                      className="radar-hint-level-badge"
                      style={{
                        backgroundColor: LEVEL_COLORS[data.achievedLevel]?.light,
                        color: LEVEL_COLORS[data.achievedLevel]?.bg,
                        border: `1px solid ${LEVEL_COLORS[data.achievedLevel]?.bg}`,
                      }}
                    >
                      Level {data.achievedLevel}
                    </span>
                    <span className="radar-hint-arrow">→</span>
                    <span
                      className="radar-hint-level-badge"
                      style={{
                        backgroundColor: LEVEL_COLORS[data.nextLevel]?.bg,
                        color: "#fff",
                      }}
                    >
                      Level {data.nextLevel}
                    </span>
                    <span className="radar-hint-pts">
                      +{data.pointsToNextLevel} pts needed
                    </span>
                  </span>
                </summary>
                <div className="radar-hint-body">
                  {data.improvementHints.length === 0 ? (
                    <p className="radar-hint-empty">
                      All questions in this dimension are already answered with
                      the highest option.
                    </p>
                  ) : (
                    <ul className="radar-hint-questions">
                      {data.improvementHints.map((hint) => (
                        <li key={hint.questionId} className="radar-hint-question">
                          <div className="radar-hint-question-header">
                            <span className="radar-hint-question-title">
                              {hint.question}
                            </span>
                            <span className="radar-hint-gain">
                              up to +{hint.potentialGain} pts
                            </span>
                          </div>
                          <div className="radar-hint-answer-row">
                            <span className="radar-hint-answer-label">Current</span>
                            <span className="radar-hint-answer-text">
                              {hint.currentAnswer ?? "Not answered yet"}
                              {hint.currentAnswer && ` (${hint.currentPoints} pts)`}
                            </span>
                          </div>
                          {hint.nextOption && (
                            <div className="radar-hint-answer-row">
                              <span className="radar-hint-answer-label radar-hint-answer-label--next">
                                Next step
                              </span>
                              <span className="radar-hint-answer-text">
                                {hint.nextOption.text} ({hint.nextOption.points} pts)
                              </span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    className="radar-hint-cta"
                    onClick={() =>
                      navigate("/fragenbogen", {
                        state: { filterDimension: dim },
                      })
                    }
                  >
                    Improve answers in questionnaire →
                  </button>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default NextSteps;
